import React, { useEffect, useState } from 'react';
import { BarChart3 } from 'lucide-react';

interface StatusStat { status: string; n: number; }

export const MediaplanPage: React.FC = () => {
    const [stats, setStats] = useState<StatusStat[]>([]);
    const [pillarMix, setPillarMix] = useState<Record<string, number>>({});
    const [channelMix, setChannelMix] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('/api/admin/content?limit=500', { credentials: 'include' })
            .then(r => r.json())
            .then(d => {
                setStats(d.stats || []);
                const slots = d.slots || [];
                const p: Record<string, number> = {};
                const c: Record<string, number> = {};
                for (const s of slots) {
                    if (s.pillar) p[s.pillar] = (p[s.pillar] || 0) + 1;
                    if (s.channel) c[s.channel] = (c[s.channel] || 0) + 1;
                }
                setPillarMix(p);
                setChannelMix(c);
            })
            .finally(() => setLoading(false));
    }, []);

    const total = stats.reduce((s, x) => s + x.n, 0);
    const card = (label: string, value: number | string, sub?: string) => (
        <div className="bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark p-4">
            <div className="text-xs uppercase text-text-secondary-light dark:text-text-secondary-dark tracking-wider mb-1">{label}</div>
            <div className="text-2xl font-bold">{value}</div>
            {sub && <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">{sub}</div>}
        </div>
    );

    return (
        <div>
            <h1 className="text-2xl font-bold mb-6">Mediaplan</h1>

            {loading && <div className="text-sm">Ładowanie…</div>}

            {!loading && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {card('Wszystkich slotów', total)}
                        {card('Drafts', stats.find(s => s.status === 'draft')?.n || 0, 'czeka na akceptację')}
                        {card('Approved', stats.find(s => s.status === 'approved')?.n || 0, 'zaplanowane')}
                        {card('Published', stats.find(s => s.status === 'published')?.n || 0, 'wysłane na FB/IG')}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark p-4">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <BarChart3 className="w-4 h-4" /> Pillar mix
                            </h3>
                            {Object.entries(pillarMix).length === 0 ? (
                                <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Brak danych</div>
                            ) : (
                                Object.entries(pillarMix).map(([k, v]) => {
                                    const pct = total ? Math.round((v / total) * 100) : 0;
                                    return (
                                        <div key={k} className="mb-2">
                                            <div className="flex justify-between text-xs mb-0.5">
                                                <span className="font-semibold">{k}</span>
                                                <span>{v} ({pct}%)</span>
                                            </div>
                                            <div className="h-2 bg-bkg-light dark:bg-bkg-dark rounded-full">
                                                <div className="h-full bg-primary-light dark:bg-primary-dark rounded-full" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div className="text-[10px] text-text-secondary-light/70 dark:text-text-secondary-dark/70 mt-3">
                                Target v3.1: DEMO 35%, PROOF 25%, EDUCATION 25%, OFFER 15%
                            </div>
                        </div>
                        <div className="bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark p-4">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <BarChart3 className="w-4 h-4" /> Channel mix
                            </h3>
                            {Object.entries(channelMix).length === 0 ? (
                                <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Brak danych</div>
                            ) : (
                                Object.entries(channelMix).map(([k, v]) => {
                                    const pct = total ? Math.round((v / total) * 100) : 0;
                                    return (
                                        <div key={k} className="mb-2">
                                            <div className="flex justify-between text-xs mb-0.5">
                                                <span className="font-semibold capitalize">{k}</span>
                                                <span>{v} ({pct}%)</span>
                                            </div>
                                            <div className="h-2 bg-bkg-light dark:bg-bkg-dark rounded-full">
                                                <div className="h-full bg-primary-light dark:bg-primary-dark rounded-full" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark p-4">
                        <h3 className="font-semibold mb-3">Status breakdown</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                            {stats.map(s => (
                                <div key={s.status} className="flex justify-between p-2 bg-bkg-light dark:bg-bkg-dark rounded-md">
                                    <span className="capitalize">{s.status}</span>
                                    <span className="font-bold">{s.n}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
